import { type ComputedRef, type Ref, computed, shallowRef, watch } from "vue";
import { NotificationSubscriber } from "../lib/subscriber";
import { PWA_EVENTS } from "../pwa.events";
import type { INotificationProvider } from "../pwa.types";

type UseSubscribePushNotificationOptions = {
  onSubscribeError?: (error: unknown) => void;
  onUnSubscribeError?: (error: unknown) => void;
  onCreateSubscriberError?: (error: unknown) => void;
  type: ComputedRef<INotificationProvider> | Ref<INotificationProvider>;
  forceSubscribe?: boolean;
  forceUnsubscribe?: boolean;
};

export function useSubscribePushNotification(opts: UseSubscribePushNotificationOptions) {
  const workerRegistered = PWA_EVENTS.WorkerRegistered.useObserve();
  const access = PWA_EVENTS.NotificationAccess.useObserve();
  const accessed = computed(() => access.value == "granted");
  const subscribed = PWA_EVENTS.NotificationSubscribed.useObserve();
  const subscriber = shallowRef<NotificationSubscriber | null>(null);
  const canSubscribe = computed(
    () => subscriber.value && workerRegistered.value && accessed.value && !subscribed.value,
  );
  const canUnsubscribe = computed(
    () => subscriber.value && workerRegistered.value && accessed.value,
  );

  async function subscribe() {
    try {
      await subscriber.value?.subscribe?.(opts.forceSubscribe);
    } catch (error) {
      opts.onSubscribeError?.(error);
    }
  }

  async function unsubscribe() {
    try {
      await subscriber.value?.unsubscribe?.(opts.forceUnsubscribe);
    } catch (error) {
      opts.onUnSubscribeError?.(error);
    }
  }

  async function createSubscriber(type: INotificationProvider) {
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) throw new Error("couldn't get registration");
      const subscriberInstance = new NotificationSubscriber({
        type,
        registration,
      });
      await subscriberInstance.init();
      subscriber.value = subscriberInstance;
    } catch (error) {
      opts.onCreateSubscriberError?.(error);
    }
  }

  watch(
    () => [opts.type.value, workerRegistered.value] as const,
    ([type, worker]) => {
      if (!worker) return;
      void createSubscriber(type);
    },
    { immediate: true },
  );

  return {
    canSubscribe,
    canUnsubscribe,
    subscribe,
    unsubscribe,
  };
}
